import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion, useInView, useAnimation } from "framer-motion";
import { 
  BarChart3, 
  CheckCircle2, 
  ChevronRight, 
  FileText, 
  Globe, 
  PieChart, 
  ShieldCheck, 
  Users 
} from "lucide-react";

import { NavBar } from "@/components/navbar";
import {Footer} from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Helmet } from "react-helmet-async";
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger 
} from "@/components/ui/accordion";

const Index2 = () => {
  const { t } = useTranslation("common");
  
  // Animation controls for scroll animations
  const controls = useAnimation();
  const featuresRef = useRef(null);
  const featuresInView = useInView(featuresRef, { once: true });
  
  useEffect(() => {
    document.title = t("index.pageTitle");
    
    if (featuresInView) {
      controls.start("visible");
    }
  }, [t, controls, featuresInView]);

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{t("index.pageTitle")}</title>
        <meta name="description" content="AI-Powered CSRD Compliance Platform - Transforming sustainability reporting with advanced AI and ESG expertise" />
      </Helmet>
      
      <NavBar />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-50 to-white pt-24 pb-16">
        <div className="absolute inset-0 z-0 opacity-40">
          <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <pattern id="grid" width="8" height="8" patternUnits="userSpaceOnUse">
                <path d="M 8 0 L 0 0 0 8" fill="none" stroke="rgba(15, 61, 43, 0.05)" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-br from-[#1A1F2C] to-[#0F3D2B] bg-clip-text text-transparent">
                {t("hero.title")}
              </h1>
              <p className="text-xl text-slate-700 mb-8 leading-relaxed">
                {t("hero.description")}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/book-demo">
                  <Button size="lg" className="bg-emerald-700 hover:bg-emerald-800 text-white">
                    {t("hero.getStarted")}
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/about">
                  <Button size="lg" variant="outline" className="border-emerald-700 text-emerald-700 hover:bg-emerald-50">
                    {t("hero.learnMore")}
                  </Button>
                </Link>
              </div>
              
              <div className="mt-8">
                <p className="text-sm text-slate-600 mb-4">Trusted by companies across Europe</p>
                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-6">
                  {[
                    { name: "Amazon", path: "/logos/amazon.png", className: "h-8" },
                    { name: "Ant Design", path: "/logos/ant.png", className: "h-7" },
                    { name: "Beet", path: "/logos/beet.png", className: "h-7" },
                    { name: "EI", path: "/logos/ei.png", className: "h-8" }
                  ].map((logo, i) => (
                    <div 
                      key={i} 
                      className="relative grayscale hover:grayscale-0 transition-all duration-300 opacity-75 hover:opacity-100"
                    >
                      <img 
                        src={logo.path} 
                        alt={`${logo.name} logo`} 
                        className={`${logo.className} w-auto object-contain`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-tr from-emerald-200/30 to-emerald-300/10 rounded-xl transform rotate-3" />
              <div className="relative rounded-xl overflow-hidden shadow-2xl border border-slate-200">
                <img
                  src="/screens/login_screen.png"
                  alt="Verdea dashboard view"
                  className="w-full h-auto object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Feature Tabs Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-900">
              {t("features.title")}
            </h2>
            <p className="text-lg text-slate-600">
              {t("features.subtitle")}
            </p>
          </motion.div>

          <div className="relative max-w-4xl mx-auto">
            {/* Apple-style Tabs */}
            <Tabs defaultValue="comparisons" className="w-full">
              {/* Apple-inspired Tab Navigation */}
              <div className="relative flex justify-center mb-16">
                <div className="absolute bottom-0 w-full border-b border-gray-200"></div>
                <TabsList className="relative bg-transparent border-0 flex space-x-8 p-0 z-10">
                  {[
                    { value: "comparisons", label: t("featureTabs.comparisons.title") },
                    { value: "insights", label: t("featureTabs.insights.title") },
                    { value: "validation", label: t("featureTabs.validation.title") }
                  ].map((tab) => (
                    <TabsTrigger 
                      key={tab.value}
                      value={tab.value}
                      className="px-1 py-2 text-base font-medium text-gray-500 border-b-2 border-transparent data-[state=active]:border-emerald-600 data-[state=active]:text-emerald-700 transition-all duration-200 bg-transparent hover:text-emerald-600 rounded-none focus-visible:outline-none focus-visible:ring-0"
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>
              
              {/* Tab Content with Apple-like Styling */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="relative z-0"
              >
                <TabsContent value="comparisons" className="mt-0 w-full">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white rounded-2xl p-8 shadow-[0_10px_60px_-15px_rgba(0,0,0,0.1)] border border-gray-100"
                  >
                    <div className="grid md:grid-cols-2 gap-12">
                      <div>
                        <h3 className="text-2xl font-semibold mb-4 text-slate-900">{t("featureTabs.comparisons.heading")}</h3>
                        <p className="text-slate-600 mb-8 leading-relaxed">
                          {t("featureTabs.comparisons.description")}
                        </p>
                        <ul className="space-y-4">
                          {[
                            "Automated data extraction from documents",
                            "AI-powered gap analysis and recommendations",
                            "Real-time industry benchmarking",
                            "Customizable reporting templates",
                            "Stakeholder engagement tools"
                          ].map((item, i) => (
                            <li key={i} className="flex items-start">
                              <div className="bg-emerald-50 rounded-full p-1 mr-3 flex-shrink-0">
                                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                              </div>
                              <span className="text-slate-700">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="relative rounded-xl overflow-hidden shadow-sm bg-slate-50 p-1">
                        <img 
                          src="/screens/dashboard_overview.png" 
                          alt="CSRD Reporting Dashboard" 
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                    </div>
                  </motion.div>
                </TabsContent>
                
                <TabsContent value="insights" className="mt-0 w-full">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white rounded-2xl p-8 shadow-[0_10px_60px_-15px_rgba(0,0,0,0.1)] border border-gray-100"
                  >
                    <div className="grid md:grid-cols-2 gap-12">
                      <div>
                        <h3 className="text-2xl font-semibold mb-4 text-slate-900">{t("featureTabs.insights.heading")}</h3>
                        <p className="text-slate-600 mb-8 leading-relaxed">
                          {t("featureTabs.insights.description")}
                        </p>
                        <ul className="space-y-4">
                          {[
                            "Advanced AI analytics for sustainability data",
                            "Discover patterns and correlations in ESG metrics",
                            "Identify improvement areas and opportunities",
                            "Track progress toward sustainability goals",
                            "Generate comprehensive reports for stakeholders"
                          ].map((item, i) => (
                            <li key={i} className="flex items-start">
                              <div className="bg-emerald-50 rounded-full p-1 mr-3 flex-shrink-0">
                                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                              </div>
                              <span className="text-slate-700">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="relative rounded-xl overflow-hidden shadow-sm bg-slate-50 p-1">
                        <img 
                          src="/screens/user_profile.png"
                          alt="AI Insights Dashboard" 
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                    </div>
                  </motion.div>
                </TabsContent>
                
                <TabsContent value="validation" className="mt-0 w-full">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white rounded-2xl p-8 shadow-[0_10px_60px_-15px_rgba(0,0,0,0.1)] border border-gray-100"
                  >
                    <div className="grid md:grid-cols-2 gap-12">
                      <div>
                        <h3 className="text-2xl font-semibold mb-4 text-slate-900">{t("featureTabs.validation.heading")}</h3>
                        <p className="text-slate-600 mb-8 leading-relaxed">
                          {t("featureTabs.validation.description")}
                        </p>
                        <ul className="space-y-4">
                          {[
                            "Comprehensive verification of sustainability data",
                            "AI-assisted review of compliance documentation",
                            "Accuracy checks against CSRD requirements",
                            "Transparency in data sources and methodology",
                            "Approval workflows for collaborative verification"
                          ].map((item, i) => (
                            <li key={i} className="flex items-start">
                              <div className="bg-emerald-50 rounded-full p-1 mr-3 flex-shrink-0">
                                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                              </div>
                              <span className="text-slate-700">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="relative rounded-xl overflow-hidden shadow-sm bg-slate-50 p-1">
                        <img 
                          src="/screens/dashboard_overview.png"
                          alt="Validation Process Dashboard" 
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                    </div>
                  </motion.div>
                </TabsContent>
              </motion.div>
            </Tabs>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section ref={featuresRef} className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-900">
              Enterprise-grade Security and Privacy
            </h2>
            <p className="text-lg text-slate-600">
              Protect your CSRD data with industry-leading security measures
            </p>
          </motion.div>

          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate={controls}
            className="grid md:grid-cols-3 gap-8"
          >
            {[
              {
                icon: <ShieldCheck className="h-10 w-10 text-emerald-600" />,
                title: t("features.encryption.title"),
                description: t("features.encryption.description")
              },
              {
                icon: <Globe className="h-10 w-10 text-emerald-600" />,
                title: t("features.sovereignty.title"),
                description: t("features.sovereignty.description")
              },
              {
                icon: <BarChart3 className="h-10 w-10 text-emerald-600" />,
                title: t("features.analytics.title"),
                description: t("features.analytics.description")
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border border-slate-200"
              >
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-3 text-slate-900">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      {/* FAQ Section */}
<section className="py-20 bg-white">
  <div className="container mx-auto px-4">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="text-center max-w-3xl mx-auto mb-16"
    >
      <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-900">
        Frequently Asked Questions
      </h2>
      <p className="text-lg text-slate-600">
        Get answers to common questions about Verdea's platform and EU sustainability reporting requirements.
      </p>
    </motion.div>

    <div className="max-w-3xl mx-auto">
      <Accordion type="single" collapsible className="w-full">
        {[
          {
            question: "What EU sustainability reporting requirements does Verdea support?",
            answer: "Verdea supports a comprehensive range of EU sustainability directives and standards including CSRD, SFDR, EU Taxonomy, NFRD, and emerging ESG frameworks. Our platform is designed to adapt to the evolving regulatory landscape, ensuring your reporting remains compliant as new requirements are introduced. The system automatically updates when regulatory changes occur, giving you peace of mind that you're always working with the latest standards."
          },
          {
            question: "How does Verdea's AI technology transform sustainability reporting?",
            answer: "Our AI-powered platform combines Retrieval Augmented Generation (RAG) with fine-tuned Large Language Models to automate the collection, processing, and analysis of sustainability data. The system extracts relevant information from various document formats, organizes it according to required standards, and generates benchmarking insights. Our knowledge graph technology creates semantic connections between data points, enabling more nuanced analysis and revealing patterns that would be difficult to identify manually. This approach dramatically reduces reporting time while improving accuracy and providing deeper insights."
          },
          {
            question: "Which organizations need to comply with EU sustainability reporting requirements?",
            answer: "EU sustainability reporting requirements apply to various organizations based on size, public interest, and operations. This includes large companies (over 250 employees or €40M turnover), listed SMEs (except micro-enterprises), financial institutions, and non-EU companies with substantial EU operations. Implementation follows a phased approach, with different categories of companies required to comply at different times between 2025 and 2027. Verdea helps organizations identify which requirements apply to them and guides them through the compliance process based on their specific circumstances."
          },
          {
            question: "How does Verdea help with benchmarking and performance analysis?",
            answer: "Verdea goes beyond basic compliance to provide comprehensive benchmarking capabilities. Our platform analyzes your sustainability performance against industry peers, regional averages, and best practices. This enables organizations to identify areas for improvement, track progress over time, and develop more effective sustainability strategies. The benchmarking functionality covers environmental metrics (like emissions and resource usage), social indicators (such as diversity and labor practices), and governance factors—giving you a holistic view of your ESG performance compared to relevant standards and competitors."
          },
          {
            question: "How does Verdea ensure data security and privacy?",
            answer: "Security is foundational to our platform. We implement enterprise-grade encryption (TLS 1.3, AES-256) for data in transit and at rest, with all data stored in EU-based servers to ensure data sovereignty. Our solutions comply with GDPR and follow a 'privacy by design' approach. We conduct regular security audits, penetration tests, and maintain strict access controls, along with comprehensive backup and disaster recovery protocols. Your sustainability data remains secure, confidential, and accessible only to authorized personnel."
          },
          {
            question: "Can Verdea integrate with existing systems and data sources?",
            answer: "Yes, Verdea is designed for seamless integration with your existing infrastructure. Our platform connects with ERP systems, financial software, environmental management tools, and other data sources through secure APIs. We support a wide range of data formats and can automatically extract information from documents, spreadsheets, and databases. This integration capability eliminates data silos and provides a unified view of your sustainability information, making reporting more efficient and comprehensive while leveraging your existing technology investments."
          }
        ].map((faq, index) => (
          <AccordionItem key={index} value={`item-${index + 1}`}>
            <AccordionTrigger className="text-left text-lg font-medium text-slate-800 hover:text-emerald-700">
              {faq.question}
            </AccordionTrigger>
            <AccordionContent className="text-slate-600 text-base">
              {faq.answer}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </div>
</section>

      {/* Trusted By Section */}
      <section className="py-16 bg-slate-50 border-y border-slate-100">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-3 text-slate-900">
              Trusted by Leading Companies
            </h2>
            <p className="text-lg text-slate-600">
              Join hundreds of organizations that trust Verdea for their CSRD compliance needs
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-5xl mx-auto"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-12 md:gap-x-16 md:gap-y-16">
              {[
                { name: "Amazon", path: "/logos/amazon.png", className: "h-10" },
                { name: "Ant Design", path: "/logos/ant.png", className: "h-9" },
                { name: "Beet", path: "/logos/beet.png", className: "h-10" },
                { name: "EI", path: "/logos/ei.png", className: "h-10" },
                { name: "Ejaso", path: "/logos/ejaso.png", className: "h-8" },
                { name: "MM1", path: "/logos/mm1.png", className: "h-9" },
                { name: "NavFilm", path: "/logos/navfilm.png", className: "h-11" },
                { name: "VBA", path: "/logos/vba.png", className: "h-10" }
              ].map((logo, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="flex items-center justify-center"
                  whileHover={{ scale: 1.05 }}
                >
                  <div 
                    className="flex items-center justify-center p-4 bg-white rounded-lg shadow-sm border border-slate-100 w-full h-24"
                  >
                    <div className="relative grayscale hover:grayscale-0 transition-all duration-500 opacity-70 hover:opacity-100">
                      <img
                        src={logo.path}
                        alt={`${logo.name} logo`}
                        className={`${logo.className} w-auto object-contain`}
                      />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.8 }}
            className="text-center mt-12 max-w-2xl mx-auto"
          >
            <p className="text-sm text-slate-500">
              These leading companies rely on Verdea for their sustainability reporting and CSRD compliance solutions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20 bg-emerald-700 text-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Transform Your Compliance Today
            </h2>
            <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
              Join forward-thinking organizations that are streamlining
              their sustainability reporting and gaining valuable insights with Verdea.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/book-demo">
                <Button size="lg" className="bg-white text-emerald-700 hover:bg-emerald-50 min-w-[180px]">
                  Book a Demo
                </Button>
              </Link>

            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Index2;
